/****************************************************
 * Module Name:  uploadStockModal.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/

import React, { useState } from "react";
import Modal from "react-bootstrap/Modal";
import Constants from "../../utility/constants";
import "bootstrap/dist/css/bootstrap.min.css";
import { Button, Box, FormControl, InputLabel, Select, MenuItem, Tooltip, Typography, IconButton, TextField } from '@mui/material';
import CommonSnackbar from "../common/commonSnackBar";
import logo from '../../images/pansuriya.jpg'
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CloseIcon from '@mui/icons-material/Close';
import Utility from "../../utility/utility";
import { LoadingButton } from "@mui/lab";
import SaveIcon from '@mui/icons-material/Save';


const UploadStockModal = ({
    show,
    setShow,
    loading,
    userOptions,
    showUploadError,
    setShowUploadError,
    handleUpload,
}) => {

    const handleClose = () => setShow(false);
    const closeErrorDialog = () => setShowUploadError(false);

    const [stoneType, setStoneType] = useState('');
    const [treatmentType, setTreatmentType] = useState('');
    const [user, setUser] = useState('');
    const [excelType, setExcelType] = useState('Regular');
    const [selectedFile, setSelectedFile] = useState(null);
    const [filtertype, setFilterType] = useState('');
    const [days, setDays] = useState('');
    const [selectedFileName, setSelectedFileName] = useState('');
    const [daysValidationError, setDaysValidationError] = useState('');

    const handleStoneTypeChange = (event) => {
        setStoneType(event.target.value);
    };

    const handleUserChange = (event) => {
        setUser(event.target.value);
    };

    const handleExcelTypeChange = (event) => {
        setExcelType(event.target.value);
    };

    const handleFilterTypeChange = (event) => {
        setFilterType(event.target.value);
    };

    const handleDaysChange = (event) => {
        setDays(event.target.value);
        Utility.CheckIfNumberNegative(event.target.value) ? setDaysValidationError("Days should be positive number") : setDaysValidationError('');
    }


    const handleFileChange = (event) => {
        setSelectedFile(event.target.files[0]);
        setSelectedFileName(event.target.files[0] ? event.target.files[0].name : '');
    };

    const handleSave = () => {
        if (selectedFile && stoneType && filtertype && days && excelType && user) {
            handleUpload(selectedFile, stoneType, filtertype, days, excelType, user, treatmentType);
        } else {
            console.log('Please select a file and stone type and clarity type');
        }
    };


    const buttonDisable = () => {
        if (filtertype.length === 0 || stoneType.length === 0 || selectedFileName.length === 0 || days.length === 0 || user.length === 0)
            return true;

        return false;
    };


    const handleClear = () => {
        setSelectedFile(null);
        setSelectedFileName('');
    };

    return (
        <div>
            <CommonSnackbar
                show={showUploadError}
                message={
                    <Box>
                        Something went wrong
                        <br /> Please try again later.
                    </Box>
                }
                variant={"error"}
                autoHide={false}
                handleClose={closeErrorDialog}
                title="Response Error"
            />
            <Modal
                backdrop="static"
                show={show}
                onHide={handleClose}
                aria-labelledby="modal-title"
                aria-describedby="modal-description"
                enforceFocus={false}
            >
                <Box sx={{ p: 0, bgcolor: 'background.paper', borderRadius: 15 }}>
                    <Modal.Header closeButton>
                        <Modal.Title>
                            <img
                                src={logo}
                                height="40pt"
                                width="40pt"
                                alt={Constants.COMPANY_SITE}
                            />
                            &nbsp;&nbsp; Upload stock
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <FormControl fullWidth
                            size="small" sx={{ m: 0, marginBottom: 2 }}>
                            <InputLabel id="stone-type-label">Stone Type</InputLabel>
                            <Select
                                labelId="stone-type-label"
                                value={stoneType}
                                onChange={handleStoneTypeChange}
                                label="Stone Type"
                            >
                                <MenuItem value="Lab Grown Diamonds">Lab Grown Diamonds</MenuItem>
                                <MenuItem value="Natural Diamonds">Natural Diamonds</MenuItem>
                            </Select>
                        </FormControl>
                        {
                            stoneType === "Lab Grown Diamonds" ? (
                                <FormControl fullWidth size="small" sx={{ mb: 3 }}>
                                    <InputLabel id="treatment-type-label">Treatment Type</InputLabel>
                                    <Select
                                        labelId="treatment-type-label"
                                        value={treatmentType}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            setTreatmentType(value);
                                        }}
                                        label="Treatment Type"
                                    >
                                        {Object.entries(Constants.TRREATMENT_TYPE).map(([key, value]) => (
                                            <MenuItem key={key} value={value}>
                                                {value}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            ) : null
                        }
                        <FormControl fullWidth
                            size="small" sx={{ m: 0 }}>
                            <InputLabel id="filter-type-label">Filter Type</InputLabel>
                            <Select
                                labelId="filter-type-label"
                                value={filtertype}
                                onChange={handleFilterTypeChange}
                                label="Filter Type"
                            >
                                <MenuItem value="Similar or Better">Similar or Better</MenuItem>
                                <MenuItem value="Exact">Exact</MenuItem>
                            </Select>
                        </FormControl>
                        <TextField fullWidth
                            size="small" sx={{ mb: 1 }}
                            margin="normal"
                            label="Every (days)"
                            placeholder="Enter Every (days)"
                            value={days}
                            onChange={handleDaysChange}
                            helperText={daysValidationError}
                            error={daysValidationError.length > 0}
                        />
                        <Typography variant="body2" sx={{ mb: 2 }}>
                            Note: 0 means once
                        </Typography>
                        <FormControl fullWidth
                            size="small" sx={{ m: 0, marginBottom: 2 }}>
                            <InputLabel id="excel-type-label">Excel Type</InputLabel>
                            <Select
                                labelId="excel-type-label"
                                value={excelType}
                                onChange={handleExcelTypeChange}
                                label="Excel Type"
                            >
                                <MenuItem value="Regular">Regular</MenuItem>
                                <MenuItem value="Stock">Stock</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControl fullWidth
                            size="small" sx={{ m: 0, marginBottom: 2 }}>
                            <InputLabel id="user-label">User</InputLabel>
                            <Select
                                labelId="user-label"
                                value={user}
                                onChange={handleUserChange}
                                label="User"
                            >
                                {userOptions && userOptions.length > 0 ? (
                                    userOptions.map((option) => (
                                        <MenuItem value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))
                                ) : (
                                    <MenuItem disabled>No Users Available</MenuItem>
                                )}
                            </Select>
                        </FormControl>
                        <Box sx={{ mt: 2, mb: 4 }}>
                            <Tooltip title="Upload File">
                                <Button
                                    variant="outlined"
                                    component="label"
                                    startIcon={<UploadFileIcon />}
                                >
                                    Upload File
                                    <input
                                        id="file-input"
                                        hidden
                                        accept=".csv"
                                        multiple
                                        type="file"
                                        onChange={handleFileChange}
                                        onClick={(e) => handleClear()}
                                    />
                                </Button>
                            </Tooltip>
                            {selectedFileName && (
                                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                                    <Typography variant="body2" sx={{ mr: 1 }}>
                                        {selectedFileName}
                                    </Typography>
                                    <IconButton
                                        size="small"
                                        onClick={handleClear}
                                    >
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            )}
                        </Box>
                    </Modal.Body>
                    <Modal.Footer>
                        <LoadingButton
                            variant="contained"
                            color="success"
                            sx={{ mr: 2 }}
                            disabled={buttonDisable()}
                            loading={loading}
                            loadingPosition="start"
                            startIcon={<SaveIcon />}
                            onClick={() => {
                                handleSave(selectedFile);
                                setShow(false);
                            }}>
                            Save
                        </LoadingButton>
                        <Button variant="contained" color="error" onClick={handleClose} disabled={loading}>
                            Close
                        </Button>
                    </Modal.Footer>
                </Box>
            </Modal>
        </div>
    );
};

export default UploadStockModal;


